import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { brandSelectTheme } from '../Common/reactSelectTheme';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';

import {
    ROLE_LABELS,
    AMOUNT_LABELS,
    validateRuleForm,
    buildRulePayload,
    conditionsSummary,
    actionSummary,
} from '../../../services/RulesService';

/**
 * Create/edit modal for a bokföringsregel — SIMPLE-FIRST layout (owner
 * 2026-07-28: "so simple someone's grandmother could figure it out").
 *
 * The visible form asks three plain questions:
 *   1. När?          — riktning (Alla/In/Ut) + "texten innehåller …"
 *   2. Bokför så här — the kontering rows
 *   3. Automatik     — bokför utan att fråga + beloppstak
 * A live preview sentence shows the rule being built, in the exact
 * vocabulary the rules list uses. Everything power-user (namn, rail,
 * prioritet, börjar/slutar-med, beloppsintervall, serie, mall) lives
 * under a collapsed "Avancerat". The name auto-derives from the text
 * condition when left empty.
 *
 * Controlled by the parent: `rule` = null → create, otherwise edit.
 * `onSave(payload)` receives the API-ready body (buildRulePayload) and
 * owns the request; `saving` disables the form while it runs.
 */

const emptyRow = (side = 'debit') => ({
    mode: 'account',
    account_number: '',
    account_name: '',
    role: '',
    side,
    amount: 'full',
});

const rowFromApi = (row) => ({
    mode: row?.role ? 'role' : 'account',
    account_number: row?.account_number ? String(row.account_number) : '',
    account_name: '',
    role: row?.role || '',
    side: row?.side || 'debit',
    amount: row?.amount || 'full',
});

const formFromRule = (rule) => ({
    name: rule?.name || '',
    active: rule ? rule.active !== false : true,
    statement_source: rule?.statement_source || 'bank',
    priority: rule?.priority != null ? String(rule.priority) : '100',
    conditions: {
        text_contains: rule?.conditions?.text_contains || '',
        text_starts: rule?.conditions?.text_starts || '',
        text_ends: rule?.conditions?.text_ends || '',
        amount_min: rule?.conditions?.amount_min != null ? String(rule.conditions.amount_min) : '',
        amount_max: rule?.conditions?.amount_max != null ? String(rule.conditions.amount_max) : '',
        direction: rule?.conditions?.direction || '',
    },
    rows: rule?.action?.rows?.length
        ? rule.action.rows.map(rowFromApi)
        : [emptyRow('debit'), emptyRow('credit')],
    series: rule?.action?.series || '',
    description_template: rule?.action?.description_template || '',
    auto_apply: Boolean(rule?.auto_apply),
    amount_cap: rule?.amount_cap != null ? String(rule.amount_cap) : '',
});

// A rule left unnamed names itself after what it matches.
const deriveName = (form) => {
    const key = form.conditions.text_contains
        || form.conditions.text_starts
        || form.conditions.text_ends;
    return key ? key.trim() : '';
};

const labelStyle = { fontSize: '0.875rem', fontWeight: 500 };
const sectionStyle = { fontSize: '0.9375rem', fontWeight: 600, color: '#1f2937', marginBottom: 8 };

const DIRECTION_CHOICES = [
    { value: '', label: 'Alla' },
    { value: 'in', label: 'Pengar in' },
    { value: 'out', label: 'Pengar ut' },
];

export default function RuleFormModal({ show, onHide, rule, accounts, onSave, saving }) {
    const isEdit = Boolean(rule?.id);
    const [form, setForm] = useState(() => formFromRule(rule));
    const [errors, setErrors] = useState({});
    // Advanced opens automatically when the rule already uses an
    // advanced field — never hide the user's own data.
    const usesAdvanced = (f) => Boolean(
        f.conditions.text_starts || f.conditions.text_ends
        || f.conditions.amount_min || f.conditions.amount_max
        || f.series || f.description_template
        || f.statement_source !== 'bank'
        || (f.priority !== '100' && f.priority !== ''),
    );
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Re-seed whenever the modal opens for a (different) rule.
    useEffect(() => {
        if (show) {
            const seeded = formFromRule(rule);
            setForm(seeded);
            setErrors({});
            setShowAdvanced(usesAdvanced(seeded));
        }
    }, [show, rule]);

    const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const setCondition = (field, value) => setForm((prev) => ({
        ...prev,
        conditions: { ...prev.conditions, [field]: value },
    }));
    const setRow = (index, patch) => setForm((prev) => {
        const rows = [...prev.rows];
        rows[index] = { ...rows[index], ...patch };
        return { ...prev, rows };
    });

    const addRow = () => setForm((prev) => ({
        ...prev,
        // Alternate the default side so debet/kredit pairs fall out naturally.
        rows: [...prev.rows, emptyRow(prev.rows.length % 2 === 0 ? 'debit' : 'credit')],
    }));
    const removeRow = (index) => setForm((prev) => ({
        ...prev,
        rows: prev.rows.filter((_, i) => i !== index),
    }));

    const handleSubmit = () => {
        // Grandmother rule: an empty name is not an error — the rule
        // names itself after its text condition.
        const effective = form.name.trim()
            ? form
            : { ...form, name: deriveName(form) };
        const { valid, errors: nextErrors } = validateRuleForm(effective);
        setErrors(nextErrors);
        if (!valid) {
            // Name still missing means no text condition either — the
            // advanced pane holds the name field; open it so the error
            // is visible.
            if (nextErrors.name) setShowAdvanced(true);
            return;
        }
        onSave(buildRulePayload(effective));
    };

    const accountValue = (row) => {
        if (!row.account_number) return null;
        const match = (accounts || []).find((option) => option.value === String(row.account_number));
        return match || { value: String(row.account_number), label: String(row.account_number) };
    };

    // Live preview — the same sentence vocabulary as the rules list, so
    // what you build here is exactly what you'll see there.
    let preview = null;
    try {
        const payload = buildRulePayload(form.name.trim() ? form : { ...form, name: deriveName(form) || '—' });
        preview = `${conditionsSummary(payload.conditions)} → ${actionSummary(payload.action)}`;
    } catch (previewError) {
        preview = null;
    }

    return (
        <Modal show={show} onHide={() => !saving && onHide()} size="lg" centered>
            <Modal.Header closeButton={!saving}>
                <Modal.Title style={{ fontSize: '1.125rem' }}>
                    {isEdit ? 'Redigera regel' : 'Ny regel'}
                    {rule?.source === 'pack' && (
                        <Badge bg="info" className="ms-2" style={{ fontSize: '0.6875rem', verticalAlign: 'middle' }}>
                            Paket
                        </Badge>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4">
                {/* ── 1. När? ──────────────────────────────────────────── */}
                <div style={sectionStyle}>När?</div>
                {errors.conditions && (
                    <Alert variant="danger" className="py-2" style={{ fontSize: '0.8125rem' }}>
                        {errors.conditions}
                    </Alert>
                )}
                <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
                    <div className="btn-group" role="group" aria-label="Riktning">
                        {DIRECTION_CHOICES.map((choice) => (
                            <Button
                                key={choice.value || 'all'}
                                size="sm"
                                variant={form.conditions.direction === choice.value ? 'primary' : 'outline-secondary'}
                                onClick={() => setCondition('direction', choice.value)}
                            >
                                {choice.label}
                            </Button>
                        ))}
                    </div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>och texten innehåller</span>
                    <Form.Control
                        type="text"
                        value={form.conditions.text_contains}
                        onChange={(e) => setCondition('text_contains', e.target.value)}
                        placeholder="t.ex. SKATTEVERKET"
                        style={{ fontSize: '0.875rem', maxWidth: 260 }}
                        aria-label="Texten innehåller"
                    />
                </div>

                {/* ── 2. Bokför så här ─────────────────────────────────── */}
                <div style={sectionStyle}>Bokför så här</div>
                {errors.rows && (
                    <Alert variant="danger" className="py-2" style={{ fontSize: '0.8125rem' }}>
                        {errors.rows}
                    </Alert>
                )}
                {form.rows.map((row, index) => (
                    <Row key={index} className="mb-2 align-items-center gx-2">
                        <Col md={2} xs={4}>
                            <Form.Select
                                value={row.mode}
                                onChange={(e) => setRow(index, {
                                    mode: e.target.value,
                                    account_number: '',
                                    account_name: '',
                                    role: '',
                                })}
                                style={{ fontSize: '0.875rem' }}
                                aria-label="Radtyp"
                            >
                                <option value="account">Konto</option>
                                <option value="role">Roll</option>
                            </Form.Select>
                        </Col>
                        <Col md={4} xs={8}>
                            {row.mode === 'account' ? (
                                <Select
                                    options={accounts}
                                    theme={brandSelectTheme}
                                    isSearchable
                                    isClearable
                                    placeholder="Välj konto..."
                                    noOptionsMessage={() => 'Inga konton hittades'}
                                    value={accountValue(row)}
                                    onChange={(option) => setRow(index, {
                                        account_number: option ? option.account_number : '',
                                        account_name: option ? option.account_name : '',
                                    })}
                                    styles={{
                                        control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
                                        menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 5 }),
                                    }}
                                />
                            ) : (
                                <Form.Select
                                    value={row.role}
                                    onChange={(e) => setRow(index, { role: e.target.value })}
                                    style={{ fontSize: '0.875rem' }}
                                    aria-label="Roll"
                                >
                                    <option value="">Välj roll...</option>
                                    {Object.entries(ROLE_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </Form.Select>
                            )}
                        </Col>
                        <Col md={2} xs={4}>
                            <Form.Select
                                value={row.side}
                                onChange={(e) => setRow(index, { side: e.target.value })}
                                style={{ fontSize: '0.875rem' }}
                                aria-label="Sida"
                            >
                                <option value="debit">Debet</option>
                                <option value="credit">Kredit</option>
                            </Form.Select>
                        </Col>
                        <Col md={3} xs={6}>
                            <Form.Select
                                value={row.amount}
                                onChange={(e) => setRow(index, { amount: e.target.value })}
                                style={{ fontSize: '0.875rem' }}
                                aria-label="Belopp"
                            >
                                {Object.entries(AMOUNT_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={1} xs={2} className="text-end">
                            {form.rows.length > 1 && (
                                <Button
                                    variant="link"
                                    className="p-1 text-danger"
                                    onClick={() => removeRow(index)}
                                    aria-label="Ta bort rad"
                                >
                                    <Trash2 size={16} />
                                </Button>
                            )}
                        </Col>
                    </Row>
                ))}
                <Button variant="link" className="p-0 mb-3" onClick={addRow}>
                    <Plus size={14} className="me-1" />
                    Lägg till rad
                </Button>

                {/* ── 3. Automatik ─────────────────────────────────────── */}
                <div style={sectionStyle}>Automatik</div>
                <Form.Check
                    type="switch"
                    id="rule-auto-apply"
                    label="Bokför automatiskt utan att fråga"
                    checked={form.auto_apply}
                    onChange={(e) => setField('auto_apply', e.target.checked)}
                    style={{ fontSize: '0.875rem' }}
                    className="mb-2"
                />
                {form.auto_apply && (
                    <div className="d-flex align-items-center mb-2" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>men bara upp till</span>
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.amount_cap}
                            isInvalid={Boolean(errors.amount_cap)}
                            onChange={(e) => setField('amount_cap', e.target.value)}
                            placeholder="t.ex. 5000"
                            style={{ fontSize: '0.875rem', maxWidth: 140 }}
                            aria-label="Beloppstak (kr)"
                        />
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>kr</span>
                        {errors.amount_cap && (
                            <div className="invalid-feedback d-block" style={{ width: '100%' }}>{errors.amount_cap}</div>
                        )}
                        <div style={{ width: '100%', fontSize: '0.75rem', color: '#b45309' }}>
                            <AlertTriangle size={12} style={{ verticalAlign: -2 }} className="me-1" />
                            Större belopp bokförs inte automatiskt — de visas som förslag i stället.
                        </div>
                    </div>
                )}

                {/* ── Live preview: the rule as the sentence it will be ── */}
                {preview && (
                    <div
                        style={{
                            border: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: 8,
                            padding: '10px 12px', marginTop: 12, fontSize: '0.8125rem', color: '#374151',
                        }}
                    >
                        <span style={{ color: '#9ca3af' }}>Så läser regeln: </span>
                        {preview}
                    </div>
                )}

                {/* ── Avancerat (collapsed) ────────────────────────────── */}
                <Button
                    variant="link"
                    className="p-0 mt-3"
                    style={{ fontSize: '0.8125rem', textDecoration: 'none' }}
                    onClick={() => setShowAdvanced((v) => !v)}
                    aria-expanded={showAdvanced}
                >
                    {showAdvanced ? <ChevronDown size={14} className="me-1" /> : <ChevronRight size={14} className="me-1" />}
                    Avancerat
                </Button>
                {showAdvanced && (
                    <div style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 14, marginTop: 10 }}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Namn</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={form.name}
                                        isInvalid={Boolean(errors.name)}
                                        onChange={(e) => setField('name', e.target.value)}
                                        placeholder={deriveName(form) || 't.ex. Bankavgift SEB'}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                    <Form.Text style={{ fontSize: '0.75rem' }}>
                                        Lämnas tomt → regeln döps efter texten den matchar.
                                    </Form.Text>
                                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Gäller</Form.Label>
                                    <Form.Select
                                        value={form.statement_source}
                                        onChange={(e) => setField('statement_source', e.target.value)}
                                        style={{ fontSize: '0.875rem' }}
                                    >
                                        <option value="bank">Bank</option>
                                        <option value="tax_authority">Skattekonto</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Prioritet</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="1"
                                        value={form.priority}
                                        onChange={(e) => setField('priority', e.target.value)}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                    <Form.Text style={{ fontSize: '0.75rem' }}>
                                        Lägre tal prövas först.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Text börjar med</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={form.conditions.text_starts}
                                        onChange={(e) => setCondition('text_starts', e.target.value)}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Text slutar med</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={form.conditions.text_ends}
                                        onChange={(e) => setCondition('text_ends', e.target.value)}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Belopp minst (kr)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.conditions.amount_min}
                                        isInvalid={Boolean(errors.amount)}
                                        onChange={(e) => setCondition('amount_min', e.target.value)}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Belopp högst (kr)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.conditions.amount_max}
                                        isInvalid={Boolean(errors.amount)}
                                        onChange={(e) => setCondition('amount_max', e.target.value)}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                    {errors.amount && (
                                        <div className="invalid-feedback d-block">{errors.amount}</div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Serie</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={form.series}
                                        onChange={(e) => setField('series', e.target.value)}
                                        placeholder="t.ex. A"
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={9}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Beskrivningsmall</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={form.description_template}
                                        onChange={(e) => setField('description_template', e.target.value)}
                                        placeholder="t.ex. Bankavgift {{datum}}"
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
                    Avbryt
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sparar…
                        </>
                    ) : (
                        <>
                            <CheckCircle size={14} className="me-1" />
                            {isEdit ? 'Spara ändringar' : 'Skapa regel'}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
